import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import { useNavigate, Link } from 'react-router-dom'; // Import Link

// ---------- DIWALI GROUND-SPINNER OVERLAY (same as in Signup) ----------
function ChakraSpinnerOverlay({ active, label = 'Loading…' }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!active) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    let running = true;

    const DPR = Math.max(1, window.devicePixelRatio || 1);
    function resize() {
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = Math.round(w * DPR);
      canvas.height = Math.round(h * DPR);
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    }
    resize();
    window.addEventListener('resize', resize);

    let t = 0;
    const center = () => ({ x: window.innerWidth / 2, y: window.innerHeight * 0.75 });
    const sparks = [];

    function draw() {
      if (!running) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.save();
      const grd = ctx.createRadialGradient(
        canvas.width / (2 * DPR),
        canvas.height / (2 * DPR),
        0,
        canvas.width / (2 * DPR),
        canvas.height / (2 * DPR),
        Math.max(canvas.width, canvas.height) / DPR / 1.2
      );
      grd.addColorStop(0, 'rgba(0,0,0,0.15)');
      grd.addColorStop(1, 'rgba(0,0,0,0.55)');
      ctx.fillStyle = grd;
      ctx.fillRect(0, 0, canvas.width / DPR, canvas.height / DPR);
      ctx.restore();

      const { x, y } = center();

      // ground glow
      ctx.save();
      ctx.globalAlpha = 0.9;
      const gg = ctx.createRadialGradient(x, y + 4, 4, x, y + 4, 80);
      gg.addColorStop(0, 'rgba(255,180,80,0.45)');
      gg.addColorStop(1, 'rgba(255,180,80,0.0)');
      ctx.fillStyle = gg;
      ctx.beginPath();
      ctx.ellipse(x, y + 8, 120, 28, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // chakra disc
      ctx.save();
      const rOuter = 70;
      const rInner = 18;
      const spokes = 20;
      const spin = t * 0.16;
      ctx.beginPath();
      ctx.arc(x, y, rOuter, 0, Math.PI * 2);
      ctx.lineWidth = 6;
      ctx.strokeStyle = '#ffb347';
      ctx.shadowBlur = 18;
      ctx.shadowColor = '#ffb347';
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(x, y, rInner, 0, Math.PI * 2);
      ctx.fillStyle = '#fff5cc';
      ctx.shadowBlur = 12;
      ctx.shadowColor = '#ffe08a';
      ctx.fill();

      for (let i = 0; i < spokes; i++) {
        const a0 = spin + (i / spokes) * Math.PI * 2;
        const a1 = a0 + (Math.PI * 2) / spokes * 0.6;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.arc(x, y, rOuter - 8, a0, a1);
        ctx.closePath();
        const hue = 20 + ((i * 360) / spokes);
        ctx.fillStyle = `hsl(${hue}, 90%, 60%)`;
        ctx.globalAlpha = 0.95;
        ctx.fill();
      }
      ctx.restore();

      // center flame
      ctx.save();
      ctx.globalAlpha = 0.9 + Math.sin(t * 0.4) * 0.1;
      ctx.beginPath();
      ctx.arc(x, y, 10 + Math.sin(t * 0.7) * 2, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255,220,160,0.95)';
      ctx.shadowBlur = 22;
      ctx.shadowColor = '#ffd27a';
      ctx.fill();
      ctx.restore();

      // sparks
      if (t % 1 < 0.6) {
        for (let i = 0; i < 12; i++) {
          const a = spin + (i / 12) * Math.PI * 2 + (Math.random() * 0.2 - 0.1);
          const s = 2.6 + Math.random() * 1.6;
          sparks.push({
            x: x + Math.cos(a) * (rOuter - 2),
            y: y + Math.sin(a) * (rOuter - 2),
            vx: Math.cos(a) * s,
            vy: Math.sin(a) * s * 0.6 - 0.2,
            life: 40 + (Math.random() * 20) | 0,
            size: 1.6 + Math.random() * 1.6,
            color: Math.random() < 0.5 ? '#ffd966' : '#fff',
          });
        }
      }
      for (let i = sparks.length - 1; i >= 0; i--) {
        const p = sparks[i];
        p.x += p.vx;
        p.y += p.vy;
        p.vx *= 0.985;
        p.vy = p.vy * 0.985 + 0.06;
        p.life--;
        ctx.save();
        ctx.globalAlpha = Math.max(0, Math.min(1, p.life / 25)) * (0.8 + Math.random() * 0.4);
        ctx.shadowBlur = 10;
        ctx.shadowColor = p.color;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        if (p.life <= 0) sparks.splice(i, 1);
      }

      t += 1;
      rafRef.current = requestAnimationFrame(draw);
    }

    draw();
    return () => {
      running = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      window.removeEventListener('resize', resize);
    };
  }, [active]);

  if (!active) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        background: 'rgba(5,10,16,0.65)',
        backdropFilter: 'blur(3px)',
        display: 'grid',
        placeItems: 'center',
        pointerEvents: 'auto',
      }}
    >
      <canvas ref={canvasRef} style={{ display: 'block' }} />
      <div
        style={{
          position: 'fixed',
          bottom: '18vh',
          textAlign: 'center',
          color: '#ffe6b3',
          fontWeight: 700,
          letterSpacing: 0.4,
          textShadow: '0 0 18px rgba(255,200,120,.9)',
        }}
      >
        {label}
      </div>
    </div>
  );
}
// -----------------------------------------------------------------

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const nav = useNavigate();

  // NEW: loading overlay state
  const [loading, setLoading] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState('Logging in…');

  const submit = async (e) => {
    e.preventDefault();
    try {
      setLoadingLabel('Logging in…');
      setLoading(true);

      const res = await api.post('/auth/login', { email, password });
      // Save token & user to localStorage
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      // Notify the app that user changed
      window.dispatchEvent(new Event('userChanged'));
      // navigate to home
      nav('/');
    } catch (err) {
      alert(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="centered">
      <h2>Login</h2>
      <form onSubmit={submit} className="form">
        <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" type="email" required />
        <input value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" type="password" required />
        <button type="submit" disabled={loading}>Login</button>
        {/* NEW: Add Signup link under the Login button, as requested */}
        <p style={{ textAlign: 'center', marginTop: '10px' }}>
          Don't have an account? <Link to="/signup" style={{ color: 'var(--color-accent)', textDecoration: 'underline', fontWeight: 'bold' }}>Sign up here</Link>
        </p>
      </form>

      {/* Diwali chakra spinner overlay */}
      <ChakraSpinnerOverlay active={loading} label={loadingLabel} />
    </div>
  );
}
